import classNames from 'classnames';
import { MdLockOutline } from 'react-icons/md';

import TierStatusChip from '../../../../../tier-pricing/TierStatusChip';
import { applyDiscount } from '../../../../../../helpers';
import { useMultiTierCheckout } from '../../../context';
import { EventPricingTier } from 'legacy/common/types/models';
import QuantityButtons from '../../QuantityButtons';
import { QuantityChangeEvents } from '../../../types';
import ToggleTierButton from './ToggleTierButton';

import styles from '../../../../Tiers.module.scss';
import { MemberOnlyTierLabel } from '../../MemberOnlyTierLabel';

type Props = {
  ticketTier: EventPricingTier;
};

const TicketTierCard = ({ ticketTier }: Props) => {
  const {
    data: { tickets, ticketTiers, appliedDiscount, checkoutSession },
    toggleTicketTierAndSave,
    updateTicketTierQuantityAndSave
  } = useMultiTierCheckout();
  const isMemberOnlyAccess =
    ticketTier?.requiredAssociationIds?.length > 0 ||
    ticketTier?.requiredOrganizationIds?.length > 0;
  const tierPendingApproval =
    ticketTier.requiresApproval &&
    (ticketTier.requestStatus === 'pending' || !ticketTier.requestStatus);

  /**
   * Determines if a ticket tier is selected based on various conditions:
   *  - Ticket is selected and no tier requiring approval has been selected
   *  - Ticket requires approval and has been selected
   */
  const isTicketTierSelected = Boolean(
    (tickets.selectedTicketTiers[ticketTier._id] &&
      !tickets.tierRequiringApproval) ||
      (tickets.tierRequiringApproval &&
        tickets.tierRequiringApproval === ticketTier._id)
  );

  /**
   * Determines whether the tier is disabled based on various conditions:
   *  - Checkout session is currently loading
   *  - Discount data is currently loading
   *  - Ticket tier is considered disabled
   *  - A ticket tier requiring approval has been selected, and it's not this one.
   *  - Number of total tickets selected by the guest is >= to the max number of tickets allowed per transaction
   *    AND the ticket tier has not yet been selected by the guest
   *    (this check allows us to disable further tiers from being added but lets the guest decrease the number of tickets for an already selected tier).
   */
  const isTierDisabled =
    checkoutSession.isCheckoutDisabled ||
    checkoutSession.isLoadingCheckout ||
    appliedDiscount.isLoading ||
    ticketTiers.isTierDisabled(ticketTier._id) ||
    (tickets.tierRequiringApproval &&
      tickets.tierRequiringApproval !== ticketTier._id) ||
    (tickets.totalAmountOfTicketsSelected >= tickets.maxTicketsPerTransaction &&
      !tickets.selectedTicketTiers[ticketTier._id]);

  /**
   * Function called when a ticket tier is clicked.
   * It checks if the tier is disabled, if a ticket tier is already selected,
   * or if the maximum number of tickets per transaction has been reached.
   * If none of the conditions are met, it toggles the ticket tier and saves the ticket tier ID.
   */
  const onTierClick = () => {
    if (
      isTierDisabled ||
      isTicketTierSelected ||
      tickets.totalAmountOfTicketsSelected >= tickets.maxTicketsPerTransaction
    ) {
      return;
    }

    toggleTicketTierAndSave(ticketTier._id);
  };

  const onToggleApprovalTier = () => {
    tickets.toggleTierRequiringApproval(ticketTier._id);
  };

  const onQuantityChange = (event: QuantityChangeEvents) => {
    updateTicketTierQuantityAndSave(ticketTier._id, event);
  };

  return (
    <div
      data-active={isTicketTierSelected}
      onClick={tierPendingApproval ? onToggleApprovalTier : onTierClick}
      className={classNames([
        styles.tier,
        {
          [styles.activeMulti]: isTicketTierSelected,
          [styles.activeApprovalMulti]:
            tierPendingApproval && isTicketTierSelected,
          [styles.disabled]: isTierDisabled
        },
        'flex-col'
      ])}
    >
      <div className="flex justify-between flex-col">
        {ticketTier.imageUrl && (
          <img src={ticketTier.imageUrl} className="rounded-md mb-4" />
        )}
        <div className="flex">
          <div className={classNames(styles.name, 'flex-1')}>
            {ticketTier?.name}
          </div>
          <div className={styles.price}>
            {ticketTier?.private && <MdLockOutline size="20px" />}
            {appliedDiscount.data?.discount && !ticketTier?.private && (
              <span className="text-gray-400 line-through mr-2">
                {`$${ticketTier?.price?.toFixed(2)}`}
              </span>
            )}
            {ticketTier?.price === 0
              ? 'Free'
              : `$${applyDiscount(
                  ticketTier?.price,
                  appliedDiscount.data
                )?.toFixed(2)}`}
          </div>
        </div>
        {isMemberOnlyAccess && (
          <div className="mt-2 -mb-2">
            <MemberOnlyTierLabel />
          </div>
        )}
        <div>
          <div className={styles.description}>{ticketTier?.description}</div>
        </div>
        <div>
          <div className="mt-2 mb-2 flex">
            <TierStatusChip tier={ticketTier} />
          </div>
        </div>
      </div>
      {!isTierDisabled && (
        <div className="flex justify-end mt-1">
          {tierPendingApproval ? (
            <ToggleTierButton
              isSelected={Boolean(tickets.tierRequiringApproval)}
            />
          ) : (
            <QuantityButtons
              tierId={ticketTier._id}
              totalAmountOfTicketsSelected={
                tickets.totalAmountOfTicketsSelected
              }
              maxTicketsPerTransaction={tickets.maxTicketsPerTransaction}
              remainingTicketsForTier={ticketTier.remaining}
              selectedTicketTierQuantity={
                tickets.selectedTicketTiers[ticketTier._id]?.quantity || 0
              }
              onQuantityChange={onQuantityChange}
            />
          )}
        </div>
      )}
    </div>
  );
};
export default TicketTierCard;
